import storageService from './storageService';
import marketingParametersService from './restServices/marketingParametersService';

const service = {};

/**
 * Hybrid Storage Service
 *
 * This service provides a unified interface to save/update data either in local storage
 * or in the cloud using the marketing parameters service.
 *
 * Local storage structure: key: {objectType, objectId, saveTime}
 * Cloud storage uses the existing marketing parameters API
 */

/**
 * Save or update data in local storage
 * @param {string} key - The storage key
 * @param {string} objectType - The object type identifier
 * @param {string} objectId - The object ID
 * @param {Object} additionalData - Any additional data to store
 * @returns {boolean} Success status
 */
service.saveToLocal = (key, objectType, objectId, additionalData = {}) => {
  try {
    let data = {
      objectType,
      objectId,
      saveTime: new Date().toISOString(),
      ...additionalData,
    };

    storageService.setItem(key, JSON.stringify(data));
    // console.debug('HybridStorageService: Data saved to local storage', {
    //   key,
    //   data,
    // });
    return true;
  } catch (error) {
    console.debug('HybridStorageService: Error saving to local storage', error);
    return false;
  }
};

/**
 * Save or update data in the cloud using marketing parameters service
 * @param {string} objectType - The object type identifier
 * @param {string} objectId - The object ID
 * @param {Object} data - The data to save
 * @returns {Promise<Object|null>} Response object or null if failed
 */
service.saveToCloud = async (key, objectType, objectId, additionalData) => {
  try {
    const response =
      await marketingParametersService.updateMarketingParamsActivity(
        key,
        objectType,
        objectId,
        additionalData,
      );
    // console.debug('HybridStorageService: Data saved to cloud', {
    //   key,
    //   objectType,
    //   objectId,
    //   additionalData,
    // });
    return response;
  } catch (error) {
    console.debug('HybridStorageService: Error saving to cloud', error);
    return null;
  }
};

/**
 * Save or update data in both local storage and cloud
 * @param {string} key - The local storage key
 * @param {string} objectType - The object type identifier
 * @param {string} objectId - The object ID
 * @param {Object} data - The data to save
 * @param {Object} options - Options for storage behavior
 * @param {boolean} options.localOnly - If true, save only to local storage
 * @param {boolean} options.cloudOnly - If true, save only to cloud
 * @param {boolean} options.failover - If true, try cloud first, fallback to local on failure
 * @returns {Promise<Object>} Result object with success status for each storage type
 */
service.saveHybrid = async (
  key,
  objectType,
  objectId,
  data = {},
  options = {},
) => {
  const result = {
    local: {success: false, data: null},
    cloud: {success: false, data: null},
  };

  // If cloudOnly is specified, only save to cloud
  if (options.cloudOnly) {
    const cloudResponse = await service.saveToCloud(objectType, objectId, data);
    result.cloud.success = cloudResponse !== null;
    result.cloud.data = cloudResponse;
    return result;
  }

  // If localOnly is specified, only save to local storage
  if (options.localOnly) {
    result.local.success = service.saveToLocal(key, objectType, objectId, data);
    return result;
  }

  // Default behavior: save to both or handle failover
  if (options.failover) {
    // Try cloud first, fallback to local
    const cloudResponse = await service.saveToCloud(objectType, objectId, data);
    if (cloudResponse !== null) {
      result.cloud.success = true;
      result.cloud.data = cloudResponse;
    } else {
      // Fallback to local storage
      result.local.success = service.saveToLocal(
        key,
        objectType,
        objectId,
        data,
      );
    }
  } else {
    // Save to both local and cloud
    result.local.success = service.saveToLocal(key, objectType, objectId, data);
    const cloudResponse = await service.saveToCloud(objectType, objectId, data);
    result.cloud.success = cloudResponse !== null;
    result.cloud.data = cloudResponse;
  }

  return result;
};

/**
 * Get data from local storage
 * @param {string} key - The storage key
 * @returns {Object|null} Parsed data object or null if not found
 */
service.getFromLocal = (key) => {
  try {
    const data = storageService.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  } catch (error) {
    console.debug(
      'HybridStorageService: Error getting from local storage',
      error,
    );
    return null;
  }
};

/**
 * Get data from the cloud using marketing parameters service
 * @param {string} objectType - The object type identifier
 * @param {string} objectId - The object ID
 * @returns {Promise<Object|null>} Response data or null if failed
 */
service.getFromCloud = async (objectType, objectId) => {
  try {
    const response =
      await marketingParametersService.getMarketingParamsActivity(
        objectType,
        objectId,
      );
    // console.debug('HybridStorageService: Data retrieved from cloud', {
    //   objectType,
    //   objectId,
    //   response,
    // });
    return response;
  } catch (error) {
    console.debug('HybridStorageService: Error getting from cloud', error);
    return null;
  }
};

/**
 * Check if a key exists in local storage
 * @param {string} key - The storage key
 * @returns {boolean} True if key exists
 */
service.existsInLocal = (key) => {
  return storageService.isKeyExist(key);
};

/**
 * Remove data from local storage
 * @param {string} key - The storage key
 * @returns {boolean} Success status
 */
service.removeFromLocal = (key) => {
  try {
    storageService.removeItem(key);
    // console.debug('HybridStorageService: Data removed from local storage', key);
    return true;
  } catch (error) {
    console.debug(
      'HybridStorageService: Error removing from local storage',
      error,
    );
    return false;
  }
};

/**
 * Update existing data in local storage by merging with new data
 * @param {string} key - The storage key
 * @param {Object} newData - New data to merge
 * @returns {boolean} Success status
 */
service.updateLocal = (key, newData) => {
  try {
    const existingData = service.getFromLocal(key);
    if (existingData) {
      const mergedData = {
        ...existingData,
        ...newData,
        saveTime: new Date().toISOString(),
      };
      storageService.setItem(key, JSON.stringify(mergedData));
      // console.debug('HybridStorageService: Data updated in local storage', {
      //   key,
      //   mergedData,
      // });
      return true;
    } else {
      console.debug('HybridStorageService: Key not found for update', key);
      return false;
    }
  } catch (error) {
    console.debug('HybridStorageService: Error updating local storage', error);
    return false;
  }
};

/**
 * Generate a standardized key for local storage
 * @param {string} prefix - Key prefix
 * @param {string} objectType - Object type
 * @param {string} objectId - Object ID
 * @returns {string} Generated key
 */
service.generateKey = (prefix, objectType, objectId) => {
  return `${prefix}_${objectType}_${objectId}`;
};

/**
 * Sync local data to cloud for a specific key
 * @param {string} key - The local storage key
 * @returns {Promise<Object|null>} Cloud response or null if failed
 */
service.syncToCloud = async (key) => {
  try {
    const localData = service.getFromLocal(key);
    if (!localData || !localData.objectType || !localData.objectId) {
      // console.debug(
      //   'HybridStorageService: Invalid local data for sync',
      //   localData,
      // );
      return null;
    }

    const {objectType, objectId, ...dataToSync} = localData;
    const response = await service.saveToCloud(
      objectType,
      objectId,
      dataToSync,
    );

    if (response) {
      // console.debug('HybridStorageService: Successfully synced to cloud', {
      //   key,
      //   objectType,
      //   objectId,
      // });
    }

    return response;
  } catch (error) {
    console.debug('HybridStorageService: Error syncing to cloud', error);
    return null;
  }
};

const hybridStorageService = {...service};
export default hybridStorageService;
